
import RouteParams from "@/entities/RouteParams";
import RouteNames from "@/entities/RouteNames";
import CountryFlag from "vue-country-flag";
import getCountryCode from "@/utils/getCountryCode";
import {
  Contractor,
  Contractor_Rating_Aggregate,
  Project,
  User,
  Tag
} from "@/generated/graphql";
import ToggleFavorite from "@/components/favorite/ToggleFavoriteRequests.vue";
import ContractorResendInvitation from "@/components/invite/ContractorResendInvitation.vue";
import InvitationSentTooltip from "@/components/invite/InvitationSentTooltip.vue";
import ContractorRating from "@/components/rating/ContractorRatingNew.vue";
import ResourceCards from "@/components/card/ResourceCards.vue";
import getLocationInfo from "@/helpers/locations/getLocationInfo";
import BadgeColor from "@/entities/BadgeColor";
import ChatIcon from "@/components/icons/ChatIconNew.vue";
import ChatIconHover from "@/components/icons/ChatIconHoverNew.vue";
import Contractor_PROJECTS from "@/apollo/contractors/GetContractorProjects.gql";
import Badge from "@/components/common/BadgeNew.vue";
import AddContractorToProject from "@/components/contractors/AddContractorToProject.vue";
import ActivityFormModal from "@/components/projects/activities/ActivityFormModal.vue";
import { emailStatusText, managerSuspendedText } from "@/data/userStatuses";
import ResourceAvatar from "@/components/card/ResourceAvatar.vue";
import ActionSection from "@/components/card/ActionSection.vue";
import EmployeeTag from "@/components/employee/EmployeeTagNew.vue";
import { Vue, Component, Prop } from "vue-property-decorator";
import RemoveManagerContractorRequest from "@/components/contractors/RemoveManagerContractorRequestResources.vue";
import AddContractorToCompany from "@/components/contractors/AddContractorToCompanyNew.vue";
import UiButton from "@/components/button/ui-button.vue";

interface IActionDefault {
  leftTitle: string;
  leftValue: number | string;
  rightTitle: string;
  rightValue: number | string;
}

interface IActionError {
  title: string;
  description: number | string;
  actionButton: string;
}

interface Contract {
  id: string;
  deleted?: boolean;
  managers: Manager[];
}

interface Manager {
    id: string;
    approved: boolean;
  }
  
interface ContractorUser {
  contracts: Contract[];
  [key: string]: any;
}

interface IManagerProject {
  id: string;
  name: string;
}

interface IActionSection {
  state: boolean;
  default: IActionDefault;
  error: IActionError;
}

@Component({
  components: {
    ResourceCards,
    CountryFlag,
    Badge,
    ResourceAvatar,
    ChatIconHover,
    ActionSection,
    ContractorRating,
    EmployeeTag,
    ActivityFormModal,
    ContractorResendInvitation,
    InvitationSentTooltip,
    ChatIcon,
    AddContractorToProject,
    ToggleFavorite,
    RemoveManagerContractorRequest,
    UiButton,
    AddContractorToCompany,
  },
  apollo: {
    projects: {
      query: Contractor_PROJECTS,
      variables() {
        return this.conditions;
      },
      update(data) {
        const totalActiveProj = data.project.filter(
          (proj) => !proj.completed && !proj.drafted
        ).length;
        this.totalActiveProj = totalActiveProj;
        return data;
      },
    },
  },
})
export default class CompanyContractorManagerCardNew extends Vue {
  @Prop({ type: Object, required: true }) readonly contractor!: Contractor;
  @Prop({ type: Object, required: true }) readonly user!: ContractorUser;

  BadgeColor: typeof BadgeColor = BadgeColor;
  emailStatusText: string = emailStatusText;
  totalActiveProj: any = null;
  modalShown: boolean = false;
  isHoveredChat: boolean = false;
  requestSent: boolean | null = null;

  @Prop({ type: Object, required: true }) readonly currentUser!: User;
  @Prop({ type: String, required: true }) readonly userUpdatedDate!: string;
   @Prop({ type: Boolean, required: true }) readonly hasAllResourceAccess!: boolean;
  @Prop({ type: String, required: true }) readonly currentUserId!: string;
  @Prop({ type: String, required: true }) readonly companyId!: string;
  @Prop({ type: Boolean, required: true }) readonly getRoleAdmin!: boolean;
  @Prop({ type: Function, default: undefined }) readonly onActivateManager?: Function;

  get conditions(): Object {
    return {
      where: {
        project_activities: {
          contractor: {
            user_id: {
              _eq: this.contractor.user.id,
            },
          },
        },
      },
    };
  }

  showModal(): void {
    this.modalShown = true;
  }

  closeModal(): void {
    this.modalShown = false;
  }

  get appliedTag(): Tag | undefined {
    return this.contractor[0]?.tag || undefined;
  }

  get isPublic(): boolean {
    return this.contractor.user.is_public;
  }

  get isContractorRequested(): boolean {
    if (this.requestSent !== null) {
      return this.requestSent;
    }

    if (!this.isCompanyMember) {
      return false;
    }

    return this.managerContractor?.approved === false;
  }

  get isRequestContractorShown(): boolean {
    return !this.isAccessGranted && !this.isContractorRequested;
  }

  get contract(): Contract | undefined {
    return this.user!.contracts[0];
  }

  get isCompanyMember(): boolean {
    return this.contractor.user.contracts.length > 0;
  }

  get isAccessGranted(): boolean {
    if (!this.isCompanyMember) {
      return false;
    }

    return Boolean(!this.contract?.deleted && this.managerContractor?.approved);
  }
  get managerContractor(): Manager | undefined {
    const managers = this.contract?.managers;

    if (!managers || managers.length === 0) {
      return undefined;
    }

    return managers[0];
  }

get pendingInvite(): boolean | undefined {
		return this.contractor?.user?.pending_invite || undefined;
	}
  
  get getActionSectionValues(): IActionSection {
    if (this.pendingInvite) {
			return {
				state: true,
				default: {
					leftTitle: 'Internal profile',
					leftValue:
						'Profile has been internally added to database. Resource not invited.',
					rightTitle: '',
					rightValue: '',
				},
				error: {
					title: 'Internal profile',
					description:
						'Profile has been internally added to database. Resource not invited.',
					actionButton: 'internal',
				},
			};
		}
    // use this function to get the values for the action section
    const actionSectionResult: IActionSection = {
      state: this.isEmailUnconfirmed,
      default: {
        leftTitle: "Active Clients",
        leftValue: this.totalActiveProj,
        rightTitle: "",
        rightValue: '',
      },
      error: {
        title: this.emailStatusText,
        description: "",
        actionButton: "resend",
      },
    };
    return actionSectionResult;
  }

  get contractorFullName(): string {
    return `${this.contractor.user.given_name} ${this.contractor.user.family_name}`;
  }

  get contractorInitials(): string {
    return `${this.contractor.user.given_name![0]}${
      this.contractor.user.family_name![0]
    }`;
  }

  get contractorMinRate(): string | undefined {
		const minRate = this.contractor.user.profile.min_rate || this.contractor.user.user_profile_by_managements[0]?.min_rate;
	

		if (!minRate) {
			return 'n/a';
		}


		return `$${minRate}`;
	}

get contractorMaxRate(): string | undefined {
	const maxRate = this.contractor.user.profile.max_rate || this.contractor.user.user_profile_by_managements[0]?.max_rate;
		const minRate = this.contractor.user.profile.min_rate || this.contractor.user.user_profile_by_managements[0]?.min_rate;
		const rateUnit = this.contractor.user.profile.rate_unit || this.contractor.user.user_profile_by_managements[0]?.rate_unit;

		if (!maxRate) {
			return ``;
		}
		if(!maxRate && minRate) {
			return `${rateUnit}`;
		}
		return `-$${maxRate}`;
	}

get contractorRateUnit(): string | undefined {
	const minRate = this.contractor.user.profile.min_rate;
	
	const maxRate = this.contractor.user.profile.max_rate;
		const rateUnit = this.contractor.user.profile.rate_unit;

		if (!minRate && !maxRate && !rateUnit) {
			return '';
		}

		return `/${rateUnit}`;
	}
  get skills() {
    const skills = this.contractor.user.profile.skills;
    return skills;
  }

  get managerInitials(): string {
    return `${this.contractor.user.given_name![0]}${
      this.contractor.user.family_name![0]
    }`;
  }

  get managerAvatar(): string | undefined {
    return this.contractor.user.avatar || undefined;
  }

  message(): void {
    this.$router.push({
      name: "inbox",
      query: {
        recipientUserId: this.contractor.user.id,
      },
    });
  }

  get isEmailUnconfirmed(): boolean {
    return !this.contractor.user.confirmed;
  }

  get isCardDisabled(): boolean {
    return this.isEmailUnconfirmed;
  }
  get country() {
    var userCountry;
    if(this.contractor.user.profile.country !== null && this.contractor.user.profile.country !== ""){
   userCountry = this.contractor.user.profile.country
    }
    else{
      userCountry = this.contractor.user.user_profile_by_managements[0]?.country;
    }
		return getCountryCode(userCountry);
  }

  get managerFullName(): string {
    return `${this.contractor.user.given_name} ${this.contractor.user.family_name}`;
  }

get contractorPageRoute(): RouteParams<{ id: string }> {
  if (this.hasAllResourceAccess || (!this.hasAllResourceAccess && this.contractor.managers[0]?.approved)) {
    return {
      name: RouteNames.CONTRACTOR_DETAILS_ABOUT,
      params: {
        id: this.contractor.id,
      },
    };
  } else {
    return {
      name: RouteNames.CONTRACTOR_PUBLIC_PAGE,
      params: {
        id: this.contractor.user.id,
      },
    };
  }
}


  get managerLocation(): string {
  var userCountry;
    if(this.contractor.user.profile.country !== null && this.contractor.user.profile.country !== ""){
   userCountry = this.contractor.user.profile;
    }
    else if(this.contractor.user.user_profile_by_managements[0]){
      userCountry = this.contractor.user.user_profile_by_managements[0];
    }
    return userCountry ? getLocationInfo(userCountry) : '';
  }

  get managerJobTitle(): string {
    // First check if user_job_title is available (using type assertion for new GraphQL field)
    const profile = this.contractor.user?.profile as any;
    if (profile?.user_job_title?.job_title) {
      return profile.user_job_title.job_title;
    }
    
    // Fallback to legacy job_title field
    if (profile?.job_title) {
      return profile.job_title;
    }
    
    // Final fallback to first experience job title
    if (this.contractor.user?.experiences?.[0]?.job_title) {
      return this.contractor.user.experiences[0].job_title;
    }
    
    return "Job title not set";
  }

  get capitalizedManagerLocation(): string {
    if (!this.managerLocation) return '';
    return this.managerLocation.charAt(0).toUpperCase() + this.managerLocation.slice(1);
  }
}
